<template>
  <Modal :title="isEditing ? 'Edit Contact' : 'Create Contact'" :error="error" @close="emit('close')">
    <div class="dialogFormGrid">
      <div class="dialogFormField dialogFormFieldSpan2">
        <div class="dialogFormLabel">Email</div>
        <input v-model="email" class="input dialogFormInputFull" placeholder="someone@company.com" :disabled="saving" />
      </div>

      <div class="dialogFormField">
        <div class="dialogFormLabel">Name</div>
        <input v-model="name" class="input dialogFormInputFull" placeholder="Full name" :disabled="saving" />
      </div>

      <div class="dialogFormField">
        <div class="dialogFormLabel">Title</div>
        <input v-model="title" class="input dialogFormInputFull" placeholder="Job title" :disabled="saving" />
      </div>

      <div class="dialogFormField">
        <div class="dialogFormLabel">Mobile phone</div>
        <input v-model="mobilePhone" class="input dialogFormInputFull" placeholder="Mobile phone" :disabled="saving" />
      </div>

      <div class="dialogFormField">
        <div class="dialogFormLabel">LinkedIn URL</div>
        <input v-model="linkedinUrl" class="input dialogFormInputFull" placeholder="https://www.linkedin.com/in/..." :disabled="saving" />
      </div>

      <div class="dialogFormField dialogFormFieldSpan2">
        <div class="dialogFormLabel">Country</div>
        <SearchSelect
          v-model="countryCode"
          :options="countryOptions"
          placeholder="Select country..."
          search-placeholder="Search countries..."
          :disabled="saving"
        />
      </div>

      <div class="dialogFormField">
        <div class="dialogFormLabel">City</div>
        <input v-model="city" class="input dialogFormInputFull" placeholder="City" :disabled="saving" />
      </div>

      <div class="dialogFormField">
        <div class="dialogFormLabel">State / Region</div>
        <input v-model="state" class="input dialogFormInputFull" placeholder="State or region" :disabled="saving" />
      </div>
    </div>

    <template #footer>
      <button class="btn" :disabled="saving" @click="emit('close')">Cancel</button>
      <button class="btn primary" :disabled="saving" @click="submit">{{ saving ? (isEditing ? 'Saving…' : 'Creating…') : (isEditing ? 'Save' : 'Create') }}</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Modal from './Modal.vue'
import SearchSelect from './SearchSelect.vue'
import { apiFetch } from '../services/api'

type ContactLike = {
  id?: string | null
  email?: string | null
  city?: string | null
  state?: string | null
  country_code?: string | null
  display?: {
    name?: string | null
    title?: string | null
    mobile_phone?: string | null
    linkedin_url?: string | null
  } | null
}

const props = defineProps<{
  accountId: string
  contact?: ContactLike | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'saved', value: unknown): void
}>()

const saving = ref(false)
const error = ref('')
const email = ref('')
const name = ref('')
const title = ref('')
const mobilePhone = ref('')
const linkedinUrl = ref('')
const city = ref('')
const state = ref('')
const countryCode = ref('')

// Build country options from Intl.DisplayNames + a full ISO 3166-1 alpha-2 list
const _ALL_COUNTRY_CODES = [
  'AF','AX','AL','DZ','AS','AD','AO','AI','AQ','AG','AR','AM','AW','AU','AT','AZ',
  'BS','BH','BD','BB','BY','BE','BZ','BJ','BM','BT','BO','BQ','BA','BW','BV','BR',
  'IO','BN','BG','BF','BI','CV','KH','CM','CA','KY','CF','TD','CL','CN','CX','CC',
  'CO','KM','CG','CD','CK','CR','CI','HR','CU','CW','CY','CZ','DK','DJ','DM','DO',
  'EC','EG','SV','GQ','ER','EE','SZ','ET','FK','FO','FJ','FI','FR','GF','PF','TF',
  'GA','GM','GE','DE','GH','GI','GR','GL','GD','GP','GU','GT','GG','GN','GW','GY',
  'HT','HM','VA','HN','HK','HU','IS','IN','ID','IR','IQ','IE','IM','IL','IT','JM',
  'JP','JE','JO','KZ','KE','KI','KP','KR','KW','KG','LA','LV','LB','LS','LR','LY',
  'LI','LT','LU','MO','MG','MW','MY','MV','ML','MT','MH','MQ','MR','MU','YT','MX',
  'FM','MD','MC','MN','ME','MS','MA','MZ','MM','NA','NR','NP','NL','NC','NZ','NI',
  'NE','NG','NU','NF','MK','MP','NO','OM','PK','PW','PS','PA','PG','PY','PE','PH',
  'PN','PL','PT','PR','QA','RE','RO','RU','RW','BL','SH','KN','LC','MF','PM','VC',
  'WS','SM','ST','SA','SN','RS','SC','SL','SG','SX','SK','SI','SB','SO','ZA','GS',
  'SS','ES','LK','SD','SR','SJ','SE','CH','SY','TW','TJ','TZ','TH','TL','TG','TK',
  'TO','TT','TN','TR','TM','TC','TV','UG','UA','AE','GB','US','UM','UY','UZ','VU',
  'VE','VN','VG','VI','WF','EH','YE','ZM','ZW',
]

const countryOptions = computed(() => {
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' })
    return _ALL_COUNTRY_CODES
      .map(code => ({ value: code, label: `${code} · ${dn.of(code) ?? code}` }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch {
    return _ALL_COUNTRY_CODES.map(code => ({ value: code, label: code }))
  }
})

const isEditing = computed(() => Boolean(String(props.contact?.id || '').trim()))

function syncFromProps() {
  email.value = String(props.contact?.email || '').trim()
  name.value = String(props.contact?.display?.name || '').trim()
  title.value = String(props.contact?.display?.title || '').trim()
  mobilePhone.value = String(props.contact?.display?.mobile_phone || '').trim()
  linkedinUrl.value = String(props.contact?.display?.linkedin_url || '').trim()
  city.value = String(props.contact?.city || '').trim()
  state.value = String(props.contact?.state || '').trim()
  countryCode.value = String(props.contact?.country_code || '').trim().toUpperCase()
  error.value = ''
}

async function submit() {
  error.value = ''
  const accountId = String(props.accountId || '').trim()
  const contactId = String(props.contact?.id || '').trim()
  const emailValue = String(email.value || '').trim()
  const nameValue = String(name.value || '').trim()
  const titleValue = String(title.value || '').trim()
  const mobilePhoneValue = String(mobilePhone.value || '').trim()
  const linkedinUrlValue = String(linkedinUrl.value || '').trim()
  const cityValue = String(city.value || '').trim()
  const stateValue = String(state.value || '').trim()
  const countryCodeValue = String(countryCode.value || '').trim().toUpperCase()

  if (!accountId) {
    error.value = 'Account is required'
    return
  }
  if (!emailValue) {
    error.value = 'Email is required'
    return
  }

  saving.value = true
  try {
    const resp = await apiFetch(
      isEditing.value
        ? `/accounts/${encodeURIComponent(accountId)}/contacts/${encodeURIComponent(contactId)}`
        : `/accounts/${encodeURIComponent(accountId)}/contacts`,
      {
        method: isEditing.value ? 'PUT' : 'POST',
        body: {
          email: emailValue,
          name: nameValue || null,
          title: titleValue || null,
          mobile_phone: mobilePhoneValue || null,
          linkedin_url: linkedinUrlValue || null,
          city: cityValue || null,
          state: stateValue || null,
          country_code: countryCodeValue || null,
        },
      },
    )
    emit('saved', resp)
    emit('close')
  } catch (e: any) {
    error.value = e?.message || `Failed to ${isEditing.value ? 'save' : 'create'} contact`
  } finally {
    saving.value = false
  }
}

watch(() => props.contact, () => {
  syncFromProps()
}, { deep: true })

onMounted(() => {
  syncFromProps()
})
</script>
